import React, { useState, useEffect } from 'react';
import { 
  Search, Filter, MoreVertical, Edit, Eye, Shield, 
  ShieldCheck, Ban, Mail, Phone, MapPin, Calendar, Star, Clock,
  Download, Plus, UserPlus, Users, AlertTriangle, CheckCircle, Trash2,
  Package, DollarSign, TrendingUp
} from 'lucide-react';
import { useSellers } from '../../hooks/useSellers';
import { useNavigate } from 'react-router-dom';

// Import X icon for notification close button
import { X } from 'lucide-react';

// Notification component
interface NotificationProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'success' | 'warning' | 'error';
  title: string;
  message: string;
}

const Notification: React.FC<NotificationProps> = ({ isOpen, onClose, type, title, message }) => {
  if (!isOpen) return null;

  const getNotificationStyles = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      case 'error':
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-blue-600" />;
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm w-full">
      <div className={`border rounded-lg p-4 shadow-lg ${getNotificationStyles()}`}>
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium">{title}</h3>
            <p className="text-sm mt-1">{message}</p>
          </div>
          <button
            onClick={onClose}
            className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal component for supplier actions
interface ActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText: string;
  confirmColor: string;
  supplier: any;
}

const ActionModal: React.FC<ActionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  confirmColor,
  supplier
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
          <div className="mb-4">
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="flex items-center space-x-3">
                <img className="h-10 w-10 rounded-full object-cover" src={supplier?.avatar} alt={supplier?.organization} />
                <div>
                  <div className="font-medium text-gray-900">{supplier?.organization}</div>
                  <div className="text-sm text-gray-500">{supplier?.email}</div>
                  <div className="text-xs text-gray-400">{supplier?.location}</div>
                </div>
              </div>
            </div>
            <p className="text-gray-600">{message}</p>
          </div>
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className={`px-4 py-2 rounded-lg text-white transition-colors ${confirmColor}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Mock suppliers data for fallback when Supabase is not connected
const mockSuppliers = [
  {
    id: '1',
    organization: 'Bengal Textiles Ltd.',
    representative: 'Md. Rahman',
    address: '123 Textile Street, Dhaka 1000',
    email: 'contact@bengaltextiles.com',
    phone: '+880-2-9876543',
    mobile: '+880-1711-123456',
    products: 'Cotton T-Shirts, Polo Shirts, Casual Wear',
    slug: 'bengal-textiles',
    subdomain: 'bengaltextiles.madeinbd.com',
    verified: true,
    year_established: 1995,
    rating: 4.9,
    total_products: 156,
    description: 'Leading manufacturer of premium cotton garments',
    specialties: ['Cotton T-Shirts', 'Polo Shirts', 'Casual Wear'],
    location: 'Dhaka, Bangladesh',
    city: 'Dhaka',
    avatar: 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg',
    status: 'verified',
    created_at: '2023-01-15T00:00:00Z',
    reviews: 245
  },
  {
    id: '2',
    organization: 'Eco Jute Co.',
    representative: 'Ms. Fatima',
    address: '456 Jute Mill Road, Khulna 9000',
    email: 'info@ecojute.bd',
    phone: '+880-41-123456',
    mobile: '+880-1812-345678',
    products: 'Jute Bags, Handicrafts, Home Decor',
    slug: 'eco-jute-co',
    subdomain: 'ecojute.madeinbd.com',
    verified: true,
    year_established: 2005,
    rating: 4.7,
    total_products: 89,
    description: 'Sustainable jute products manufacturer',
    specialties: ['Jute Bags', 'Handicrafts', 'Home Decor'],
    location: 'Khulna, Bangladesh',
    city: 'Khulna',
    avatar: 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg',
    status: 'verified',
    created_at: '2023-02-20T00:00:00Z',
    reviews: 189
  },
  {
    id: '3',
    organization: 'Heritage Weavers',
    representative: 'Mr. Karim',
    address: '789 Heritage Lane, Dhaka 1205',
    email: 'heritage@weavers.com',
    phone: '+880-2-5551234',
    mobile: '+880-1555-987654',
    products: 'Traditional Sarees, Handloom Fabrics',
    slug: 'heritage-weavers',
    subdomain: 'heritage.madeinbd.com',
    verified: false,
    year_established: 1980,
    rating: 4.8,
    total_products: 67,
    description: 'Preserving traditional weaving techniques',
    specialties: ['Jamdani Sarees', 'Handloom Fabrics'],
    location: 'Dhaka, Bangladesh',
    city: 'Dhaka',
    avatar: 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg',
    status: 'pending',
    created_at: '2023-03-10T00:00:00Z',
    reviews: 156
  }
];

const SupplierManagement = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const { sellers: dbSellers, loading, error, deleteSeller } = useSellers();
  
  // Local state for suppliers to allow modifications
  const [suppliers, setSuppliers] = useState<any[]>([]);
  
  // Update local suppliers when database suppliers change
  useEffect(() => {
    // Use suppliers directly from the hook (already filtered)
    setSellers(dbSellers);
  }, [dbSellers]);
  
  // Notification state
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'success' as 'success' | 'warning' | 'error',
    title: '',
    message: ''
  });

  // Function to close notification
  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };

  // Function to show notification
  const showNotification = (type: 'success' | 'warning' | 'error', title: string, message: string) => {
    setNotification({ isOpen: true, type, title, message });
    // Auto-close after 5 seconds
    setTimeout(() => {
      setNotification(prev => ({ ...prev, isOpen: false }));
    }, 5000);
  };

  // Modal state
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: '',
    supplier: null as any
  });
  
  const categories = [
    'Garments & Apparel',
    'Jute & Natural Fiber',
    'Frozen Foods',
    'Leather Goods',
    'Pharmaceuticals & Health',
    'Scrap & Recycling'
  ];

  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesSearch = supplier.organization.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         supplier.representative.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (supplier.email && supplier.email.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || supplier.status === filterStatus;
    // For category filtering, we'll need to implement this differently
    // since the database schema might not have a direct category field
    const matchesCategory = filterCategory === 'all';
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const handleSelectSupplier = (supplierId: string) => {
    setSelectedSuppliers(prev => 
      prev.includes(supplierId) 
        ? prev.filter(id => id !== supplierId)
        : [...prev, supplierId]
    );
  };

  const handleSelectAll = () => {
    setSelectedSuppliers(
      selectedSuppliers.length === filteredSuppliers.length 
        ? [] 
        : filteredSuppliers.map(supplier => supplier.id)
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'verified':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <ShieldCheck className="h-3 w-3 mr-1" />
          Verified
        </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </span>;
      case 'under_review':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Eye className="h-3 w-3 mr-1" />
          Under Review
        </span>;
      case 'suspended':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <Ban className="h-3 w-3 mr-1" />
          Suspended
        </span>;
      default:
        return null;
    }
  };

  // Handle action button clicks
  const handleVerifyClick = (supplier: any) => {
    showNotification('success', 'Supplier Verified', `${supplier.organization} has been verified successfully!`);
    setModalState({
      isOpen: true,
      type: 'verify',
      supplier
    });
  };

  const handleSuspendClick = (supplier: any) => {
    showNotification('warning', 'Supplier Suspended', `${supplier.organization} has been suspended.`);
    setModalState({
      isOpen: true,
      type: 'suspend',
      supplier
    });
  };

  const handleDeleteClick = (supplier: any) => {
    setModalState({
      isOpen: true,
      type: 'delete',
      supplier
    });
  };

  const handleModalConfirm = () => {
    const { type, supplier } = modalState;
    
    switch (type) {
      case 'verify':
        showNotification('success', 'Supplier Verified', `${supplier.organization} has been verified successfully!`);
        break;
      case 'suspend':
        showNotification('warning', 'Supplier Suspended', `${supplier.organization} has been suspended.`);
        break;
      case 'delete':
        // Use the deleteSupplier function from the hook
        deleteSupplier(supplier.id);
        
        showNotification('error', 'Supplier Deleted', `${supplier.organization} has been deleted.`);
        break;
    }
    
    setModalState({ isOpen: false, type: '', supplier: null });
  };

  const handleModalClose = () => {
    setModalState({ isOpen: false, type: '', supplier: null });
  };

  // Get modal content based on action type
  const getModalContent = () => {
    const { type, supplier } = modalState;
    
    switch (type) {
      case 'verify':
        return {
          title: 'Verify Supplier',
          message: `Are you sure you want to verify this supplier? This will give them access to list products and receive orders.`,
          confirmText: 'Verify Supplier',
          confirmColor: 'bg-green-600 hover:bg-green-700'
        };
      case 'suspend':
        return {
          title: 'Suspend Supplier',
          message: `Are you sure you want to suspend this supplier? They will lose access to their account and won't be able to receive new orders.`,
          confirmText: 'Suspend Account',
          confirmColor: 'bg-yellow-600 hover:bg-yellow-700'
        };
      case 'delete':
        return {
          title: 'Delete Supplier',
          message: `Are you sure you want to permanently delete this supplier? This action cannot be undone and will remove all their data.`,
          confirmText: 'Delete Permanently',
          confirmColor: 'bg-red-600 hover:bg-red-700'
        };
      default:
        return {
          title: '',
          message: '',
          confirmText: '',
          confirmColor: ''
        };
    }
  };

  return suppliers && suppliers.length > 0 ? (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Supplier Management</h1>
              <p className="text-gray-600">Manage and verify supplier accounts</p>
            </div>
            <div className="flex items-center space-x-4">
              <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2">
                <Download className="h-4 w-4" />
                <span>Export Suppliers</span>
              </button>
              <button 
                onClick={() => navigate('/admin/merchant-acquisition')}
                className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Add Supplier</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Connection Status Notice */}
        {error && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <span>Database connection issue. Showing demo data. Please check your Supabase configuration.</span>
          </div>
        )}
        
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search suppliers by name, contact, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="all">All Status</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
                <option value="under_review">Under Review</option>
                <option value="suspended">Suspended</option>
              </select>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Suppliers</p>
                <p className="text-2xl font-bold text-gray-900">{suppliers.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <ShieldCheck className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Verified</p>
                <p className="text-2xl font-bold text-gray-900">{suppliers.filter(s => s.status === 'verified').length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending Review</p>
                <p className="text-2xl font-bold text-gray-900">{suppliers.filter(s => s.status === 'pending' || s.status === 'under_review').length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">$114.9K</p>
              </div>
            </div>
          </div>
        </div>

        {/* Suppliers Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Suppliers ({filteredSuppliers.length})</h2>
              {selectedSuppliers.length > 0 && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">{selectedSuppliers.length} selected</span>
                  <button 
                    onClick={() => {
                      alert(`Verifying ${selectedSuppliers.length} suppliers`);
                      // Here you would implement the actual verification logic
                    }}
                    className="text-sm text-green-600 hover:text-green-700 font-medium"
                  >
                    Verify
                  </button>
                  <button 
                    onClick={() => {
                      if (confirm(`Are you sure you want to suspend ${selectedSuppliers.length} suppliers?`)) {
                        alert(`Suspended ${selectedSuppliers.length} suppliers`);
                        // Here you would implement the actual suspension logic
                      }
                    }}
                    className="text-sm text-red-600 hover:text-red-700 font-medium"
                  >
                    Suspend
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedSuppliers.length === filteredSuppliers.length && filteredSuppliers.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documents</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSuppliers.map((supplier) => (
                  <tr key={supplier.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedSuppliers.includes(supplier.id)}
                        onChange={() => handleSelectSupplier(supplier.id)}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <img className="h-10 w-10 rounded-full object-cover" src={supplier.avatar} alt={supplier.organization} />
                        <div className="ml-4">
                          <div className="flex items-center">
                            <div className="text-sm font-medium text-gray-900">{supplier.organization}</div>
                            {supplier.verified && (
                              <ShieldCheck className="h-4 w-4 text-green-500 ml-2" />
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{supplier.email || 'No email provided'}</div>
                          <div className="flex items-center text-xs text-gray-400 mt-1">
                            <MapPin className="h-3 w-3 mr-1" />
                            {supplier.location}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        // Since we don't have direct category mapping, we'll use a default style
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {supplier.products ? supplier.products.split(',')[0] : 'General'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(supplier.status)}
                      <div className="text-xs text-gray-500 mt-1">
                        Joined: {new Date(supplier.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center space-x-1 mb-1">
                        <Star className="h-3 w-3 text-yellow-400 fill-current" />
                        <span className="text-xs">{supplier.rating} ({supplier.reviews})</span>
                      </div>
                      <div className="text-xs text-gray-500">{supplier.total_products} products</div>
                      <div className="text-xs font-medium text-green-600">${(supplier.total_products * 100).toFixed(2)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs text-gray-600">
                        {supplier.specialties ? supplier.specialties.length : 0} specialties
                        <div className="mt-1">
                          {supplier.specialties && supplier.specialties.slice(0, 2).map((specialty, index) => (
                            <div key={index} className="text-xs text-gray-500">• {specialty}</div>
                          ))}
                          {supplier.specialties && supplier.specialties.length > 2 && (
                            <div className="text-xs text-gray-400">+{supplier.specialties.length - 2} more</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => window.open(`/supplier/${supplier.slug}`, '_blank')}
                          className="text-gray-400 hover:text-gray-600"
                          title="View Supplier Profile"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => navigate(`/admin/suppliers/edit/${supplier.id}`)}
                          className="text-gray-400 hover:text-gray-600"
                          title="Edit Supplier"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => window.location.href = `mailto:${supplier.email}`}
                          className="text-gray-400 hover:text-gray-600"
                          title="Email Supplier"
                        >
                          <Mail className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleVerifyClick(supplier)}
                          className="text-green-400 hover:text-green-600"
                          title="Verify Supplier"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleSuspendClick(supplier)}
                          className="text-yellow-400 hover:text-yellow-600"
                          title="Suspend Supplier"
                        >
                          <AlertTriangle className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleDeleteClick(supplier)}
                          className="text-red-400 hover:text-red-600"
                          title="Delete Supplier"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => {
                            const options = ['Verify Supplier', 'Suspend Account', 'Delete Supplier', 'Send Notification'];
                            alert(`More actions for ${supplier.organization}:\n- ${options.join('\n- ')}`);
                          }}
                          className="text-gray-400 hover:text-gray-600"
                          title="More Options"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Action Modal */}
      <ActionModal
        isOpen={modalState.isOpen}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
        supplier={modalState.supplier}
        {...getModalContent()}
      />

      {/* Notification */}
      <Notification
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </div>
  ) : loading ? (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
    </div>
  ) : error ? (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-sm p-8 max-w-md">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Suppliers</h2>
          <p className="text-gray-600 mb-6">{error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    </div>
  ) : (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-sm p-8 max-w-md">
        <div className="text-center">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Suppliers Found</h2>
          <p className="text-gray-600 mb-6">There are currently no suppliers in the system.</p>
          <button 
            onClick={() => navigate('/admin/merchant-acquisition')}
            className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors flex items-center space-x-2 mx-auto"
          >
            <Plus className="h-4 w-4" />
            <span>Add First Supplier</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupplierManagement;